import {
  batch,
  createEffect,
  createMemo,
  createSignal,
  on,
  untrack,
  type Accessor,
} from 'solid-js';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

import { useAppContext } from '../AppContextProvider';
import { SidePanelTabName } from '../constants/panel-constants';
import type { SyncFxWorkspaceReferencesInput } from './fx-workspace-repository';
import { draftHasPath, draftWithoutPath } from './draft-mission-path-selection';
import {
  mapReferenceTrns,
  sameReferenceTrns,
  toPathReferenceTrn,
  toServerReferenceTrn,
  type MissionPathRef,
} from './map-mission-path-reference';

export interface FxWorkspaceDataEditingApi {
  readonly isWorkspaceDataEditing: Accessor<boolean>;
  readonly editingWorkspaceId: Accessor<string | undefined>;
  readonly isEditingWorkspaceReferencesLoaded: Accessor<boolean>;
  readonly draftReferences: Accessor<ReadonlySet<string>>;
  enterWorkspaceDataEditing(): void;
  toggleDraftReference(refTrn: string): void;
  isDraftReference(refTrn: string): boolean;
  setDraftReferenceSelection(
    refTrns: readonly string[],
    selected: boolean
  ): void;
  commitWorkspaceDataEditing(): Promise<boolean>;
  cancelWorkspaceDataEditing(): void;
  resetSession(): void;
}

function mergeLoadedReferencesIntoDraft(
  draft: ReadonlySet<string>,
  loadedReferences: ReadonlySet<string>
): Set<string> {
  const next = new Set(draft);
  for (const refTrn of loadedReferences) {
    next.add(refTrn);
  }
  return next;
}

export function useFxWorkspaceDataEditing(options: {
  activeWorkspaceId: Accessor<string | undefined>;
  getWorkspaceReferences(workspaceId: string): ReadonlySet<string>;
  areWorkspaceReferencesLoaded(workspaceId: string): boolean;
  syncWorkspaceReferences(
    workspaceId: string,
    input: SyncFxWorkspaceReferencesInput
  ): Promise<boolean>;
  getMissionPathRefs?: Accessor<readonly MissionPathRef[]>;
}): FxWorkspaceDataEditingApi {
  const appContext = useAppContext();
  const [isWorkspaceDataEditing, setIsWorkspaceDataEditing] =
    createSignal(false);
  const [editingWorkspaceId, setEditingWorkspaceId] = createSignal<
    string | undefined
  >(undefined);
  const [editableDraft, setEditableDraft] = createSignal<ReadonlySet<string>>(
    new Set<string>()
  );
  const [isDraftBaselineMerged, setIsDraftBaselineMerged] = createSignal(false);
  const missionPathRefs = (): readonly MissionPathRef[] =>
    options.getMissionPathRefs?.() ?? [];
  const toDraftPath = (refTrn: string): string =>
    toPathReferenceTrn(refTrn, missionPathRefs());
  const draftReferences = createMemo(
    (previous: ReadonlySet<string>): ReadonlySet<string> => {
      const mapped = mapReferenceTrns(editableDraft(), toDraftPath);
      return sameReferenceTrns(previous, mapped) ? previous : mapped;
    },
    new Set<string>()
  );

  const isEditingWorkspaceReferencesLoaded = createMemo((): boolean => {
    const workspaceId = editingWorkspaceId();
    return (
      workspaceId !== undefined &&
      options.areWorkspaceReferencesLoaded(workspaceId)
    );
  });

  const resetSession = (): void => {
    batch(() => {
      setIsWorkspaceDataEditing(false);
      setEditingWorkspaceId(undefined);
      setEditableDraft(new Set<string>());
      setIsDraftBaselineMerged(false);
    });
  };

  const exitEditingAndReturnToWorkspaces = (): void => {
    resetSession();
    appContext.setSelectedSidePanelTab(SidePanelTabName.Workspaces);
  };

  const mergeLoadedReferencesBaseline = (workspaceId: string): void => {
    if (
      !options.areWorkspaceReferencesLoaded(workspaceId) ||
      isDraftBaselineMerged()
    ) {
      return;
    }

    batch(() => {
      setEditableDraft((previous) =>
        mergeLoadedReferencesIntoDraft(
          previous,
          options.getWorkspaceReferences(workspaceId)
        )
      );
      setIsDraftBaselineMerged(true);
    });
  };

  createEffect(
    on(
      () => options.activeWorkspaceId(),
      (activeId) => {
        if (!untrack(isWorkspaceDataEditing)) {
          return;
        }

        if (untrack(editingWorkspaceId) !== activeId) {
          exitEditingAndReturnToWorkspaces();
        }
      }
    )
  );

  createEffect(
    on(
      () => {
        const workspaceId = editingWorkspaceId();
        return {
          isEditing: isWorkspaceDataEditing(),
          workspaceId,
          areReferencesLoaded:
            workspaceId !== undefined &&
            options.areWorkspaceReferencesLoaded(workspaceId),
        };
      },
      ({ isEditing, workspaceId, areReferencesLoaded }) => {
        if (!isEditing || workspaceId === undefined || !areReferencesLoaded) {
          return;
        }

        mergeLoadedReferencesBaseline(workspaceId);
      }
    )
  );

  const enterWorkspaceDataEditing = (): void => {
    const workspaceId = options.activeWorkspaceId();
    if (workspaceId === undefined) {
      return;
    }

    batch(() => {
      if (!isWorkspaceDataEditing() || editingWorkspaceId() !== workspaceId) {
        setIsDraftBaselineMerged(false);
        if (options.areWorkspaceReferencesLoaded(workspaceId)) {
          setEditableDraft(
            new Set(options.getWorkspaceReferences(workspaceId))
          );
          setIsDraftBaselineMerged(true);
        } else {
          setEditableDraft(new Set<string>());
        }
        setEditingWorkspaceId(workspaceId);
        setIsWorkspaceDataEditing(true);
      }

      appContext.setSelectedSidePanelTab(SidePanelTabName.ProjectData);
    });
  };

  const toggleDraftReference = (refTrn: string): void => {
    if (!isWorkspaceDataEditing()) {
      return;
    }

    const missions = missionPathRefs();
    const pathRef = toPathReferenceTrn(refTrn, missions);
    setEditableDraft((previous) => {
      if (draftHasPath(previous, pathRef, missions)) {
        return draftWithoutPath(previous, pathRef, missions);
      }

      const next = new Set(previous);
      next.add(pathRef);
      return next;
    });
  };

  const isDraftReference = (refTrn: string): boolean =>
    draftReferences().has(refTrn);

  const setDraftReferenceSelection = (
    refTrns: readonly string[],
    selected: boolean
  ): void => {
    if (!isWorkspaceDataEditing()) {
      return;
    }

    const missions = missionPathRefs();
    setEditableDraft((previous) => {
      let next = previous;
      for (const refTrn of refTrns) {
        const pathRef = toPathReferenceTrn(refTrn, missions);
        const alreadySelected = draftHasPath(next, pathRef, missions);
        if (selected && !alreadySelected) {
          const added = new Set(next);
          added.add(pathRef);
          next = added;
        } else if (!selected && alreadySelected) {
          next = draftWithoutPath(next, pathRef, missions);
        }
      }
      return next;
    });
  };

  const commitWorkspaceDataEditing = async (): Promise<boolean> => {
    const workspaceId = editingWorkspaceId();
    if (!isWorkspaceDataEditing() || workspaceId === undefined) {
      return false;
    }

    if (!options.areWorkspaceReferencesLoaded(workspaceId)) {
      NotificationEventBus.emit({
        type: 'error',
        message: t('mobileMapping.fxWorkspace.workspaceDataEditing.notReady'),
      });
      return false;
    }

    mergeLoadedReferencesBaseline(workspaceId);

    const current = options.getWorkspaceReferences(workspaceId);
    const draftOnServer = mapReferenceTrns(draftReferences(), (refTrn) =>
      toServerReferenceTrn(refTrn, missionPathRefs())
    );
    const toAdd = [...draftOnServer].filter((refTrn) => !current.has(refTrn));
    const toRemove = [...current].filter(
      (refTrn) => !draftOnServer.has(refTrn)
    );

    const saved = await options.syncWorkspaceReferences(workspaceId, {
      toAdd,
      toRemove,
    });
    if (saved) {
      exitEditingAndReturnToWorkspaces();
    }

    return saved;
  };

  const cancelWorkspaceDataEditing = (): void => {
    if (!isWorkspaceDataEditing()) {
      return;
    }

    exitEditingAndReturnToWorkspaces();
  };

  return {
    isWorkspaceDataEditing,
    editingWorkspaceId,
    isEditingWorkspaceReferencesLoaded,
    draftReferences,
    enterWorkspaceDataEditing,
    toggleDraftReference,
    isDraftReference,
    setDraftReferenceSelection,
    commitWorkspaceDataEditing,
    cancelWorkspaceDataEditing,
    resetSession,
  };
}
